'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useOptimistic,
  useTransition,
  type ReactNode,
} from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { LoadingRegion } from '@/components/ui/LoadingRegion';

type Scope = 'page' | 'calendar';
type Destination = { href: string; scope: Scope } | null;
const NavigationContext = createContext<{
  destination: Destination;
  pending: boolean;
  run: (href: string, scope: Scope, action: () => void) => void;
} | null>(null);

export function LoadingNavigationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [pending, startTransition] = useTransition();
  const [destination, setDestination] = useOptimistic<Destination>(null);
  const run = useCallback(
    (href: string, scope: Scope, action: () => void) => {
      startTransition(() => {
        setDestination({ href, scope });
        action();
      });
    },
    [setDestination]
  );
  const value = useMemo(
    () => ({ destination, pending, run }),
    [destination, pending, run]
  );
  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useLoadingNavigation() {
  return useContext(NavigationContext);
}

export function useLoadingRouter(scope: Scope = 'page') {
  const router = useRouter();
  const navigation = useLoadingNavigation();
  const run = navigation?.run;
  return useMemo(
    () => ({
      ...router,
      push(href: string, options?: Parameters<typeof router.push>[1]) {
        const action = () =>
          options === undefined
            ? router.push(href)
            : router.push(href, options);
        if (run) run(href, scope, action);
        else action();
      },
      refresh() {
        if (run) run(window.location.href, scope, () => router.refresh());
        else router.refresh();
      },
    }),
    [router, run, scope]
  );
}

/** Subsequent filter changes build on the selection in flight, not the old URL. */
export function useCalendarSearchParams() {
  const committed = useSearchParams();
  const navigation = useLoadingNavigation();
  const href =
    navigation?.destination?.scope === 'calendar'
      ? navigation.destination.href
      : null;
  return useMemo(
    () =>
      href ? new URL(href, 'https://partyamt.de').searchParams : committed,
    [href, committed]
  );
}

export function NavigationRegion({
  children,
  scope = 'page',
  dark = false,
  error,
  onRetry,
}: {
  children: ReactNode;
  scope?: Scope;
  dark?: boolean;
  error?: string | null;
  onRetry?: () => void;
}) {
  const navigation = useLoadingNavigation();
  return (
    <LoadingRegion
      pending={Boolean(
        navigation?.pending && navigation.destination?.scope === scope
      )}
      dark={dark}
      error={error}
      onRetry={onRetry}
      label={
        scope === 'calendar' ? 'Events werden geladen…' : 'Seite wird geladen…'
      }
    >
      {children}
    </LoadingRegion>
  );
}
